import { useCallback, useContext, useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { isObjEmpty } from '@src/utility/Utils';
import * as masks from '@src/utility/Mask';
import { isValidPhone } from '@brazilian-utils/brazilian-utils';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import api from '@src/services/api';
import { toastError, toastSuccess } from '@src/utility/Toast';
import { useHistory } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import { AbilityContext } from '@src/utility/context/Can';
import { useDispatch } from 'react-redux';
import { handleLogin } from '@src/redux/actions/auth';
import moment from 'moment';
import { RegisterFormData } from './RegisterFormData';

type useRegisterFormProps = {
  onChangeBlocking: (isBlocking: boolean) => void;
};

export function useRegisterForm({ onChangeBlocking }: useRegisterFormProps) {
  const ability = useContext(AbilityContext);
  const dispatch = useDispatch();
  const history = useHistory();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required('Nome é obrigatório'),
        email: yup
          .string()
          .required('Email é obrigatório')
          .email('Por favor inclua um email válido'),
        mobile: yup
          .string()
          .required('Celular é obrigatório')
          .transform(masks.phoneMask.transform)
          .test('validatePhone', 'Celular inválido', (value) => {
            if (!value) {
              return false;
            }

            return isValidPhone(value);
          }),
        password: yup
          .string()
          .required('Por favor, insira sua senha')
          .matches(
            /^.*(?=.{6,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
            'A senha deve conter pelo menos 6 caracteres, uma maiúscula, um número e um caractere especial'
          ),
        password_confirmation: yup
          .string()
          .required('Por favor, confirme sua senha')
          .when('password', {
            is: (password?: string) => !!(password && password.length > 0),
            then: yup
              .string()
              .oneOf([yup.ref('password')], 'Senha não coincide'),
          }),
        terms_of_use: yup
          .boolean()
          .required('Aceite os termos de Uso.')
          .oneOf([true]),
        terms_of_privacity: yup
          .boolean()
          .required('Aceite os termos de Política e Privacidade.')
          .oneOf([true]),
      }),
    []
  );

  const { register, handleSubmit, errors, control } = useForm<RegisterFormData>(
    {
      resolver: yupResolver(validationSchema),
    }
  );

  const onSubmit = useCallback(
    async (formValues: RegisterFormData) => {
      if (isObjEmpty(errors)) {
        try {
          if (!executeRecaptcha) {
            console.log('Execute recaptcha not yet available');
          } else {
            formValues.token_recaptcha = await executeRecaptcha('registro');
          }

          const response = await axios.get<{ ip: string }>(
            'https://api.ipify.org?format=json'
          );
          const ipdAddress = response.data.ip;
          formValues.ip_address = ipdAddress;
          formValues.acceptation_date = moment().format('YYYY-MM-DD H:m:s');

          onChangeBlocking(true);

          const { data } = await api.post('register', formValues, {
            headers: {
              'Content-Type': 'application/json',
            },
          });

          api.defaults.headers.Authorization = `Bearer ${data.token}`;

          onChangeBlocking(false);

          toastSuccess({
            title: 'Parabéns',
            description: 'Conta criada com sucesso. Falta pouco para finalizar',
          });

          const user = {
            ...data.user,
            accessToken: data.token,
            refreshToken: null,
          };

          dispatch(handleLogin(user));
          ability.update(user.ability);
          history.push('/registration/wizard');
        } catch (error) {
          onChangeBlocking(false);

          const err = error as Error | AxiosError;

          if (axios.isAxiosError(err)) {
            const messages = err.response?.data;

            if (typeof messages === 'object') {
              if (!Object.keys(messages).includes('exception')) {
                Object.keys(messages).forEach((message) => {
                  const errors = messages[message];
                  errors.forEach((error: string) => {
                    toastError({ title: 'Atenção', description: error });
                  });
                });
              } else {
                toastError({ title: 'Atenção', description: messages.message });
              }
            } else {
              toastError({
                title: 'Atenção',
                description:
                  'Algo deu errado ao cadastrar sua conta, por favor tente novamente.',
              });
            }
          } else {
            toastError({ title: 'Atenção', description: 'Server error' });
          }
        }
      }
    },
    [ability, dispatch, errors, history, onChangeBlocking]
  );

  return {
    register,
    errors,
    control,
    onSubmit: handleSubmit(onSubmit),
  };
}
